import { useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router";
import { useContextoApp } from "@/app/contexto";
import { useApiResource } from "@/hooks/useApiResource";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import { api, type Caja, type Adjunto, type Cobro, type CompradorListado, type Cuota, type Devolucion, type Modalidad, type TipoCambioSugerido, type Unidad, type VentaDetalle, type VistaPreviaPlan } from "@/lib/api";
import { cajaPorDefecto, hayVariasCajas, opcionesCaja } from "@/lib/cajas";
import { hoyISO } from "@/lib/format";
import {
  aParametrosPlan, aPayloadCobro, aPayloadCuota, aPayloadCuotas, aPayloadEdicionVenta, aPayloadNotasVenta, aPayloadTransicion, equivalenteEnCuenta, filasDesdeVistaPrevia,
  formCobro, formCuota, formPlan, formTransicion, formVenta, montoPositivo, opcionesUnidadesDisponibles, validarCobro, validarCuota, validarFilas, validarPlan, validarTransicion,
  validarVenta, type ErroresCobro, type ErroresCuota, type ErroresFilas, type ErroresPlan, type ErroresVenta, type FilaCuota, type FormCobro, type FormCuota, type FormPlan,
  type FormTransicion, type FormVenta, type PayloadCobro, type PayloadParametros,
} from "@/lib/ventas";

type Dialogo =
  | { tipo: "editar" }
  | { tipo: "borrar" }
  | { tipo: "plan" }
  | { tipo: "editar-cuota"; cuota: Cuota }
  | { tipo: "cobro"; cuota: Cuota; cobro: Cobro | null }
  | { tipo: "borrar-cobro"; cuota: Cuota; cobro: Cobro }
  | { tipo: "boleto" }
  | { tipo: "escritura" }
  | { tipo: "desistir" }
  | { tipo: "devolucion"; devolucion: Devolucion | null }
  | { tipo: "borrar-devolucion"; devolucion: Devolucion }
  | null;

type Transicion = "boleto" | "escritura" | "desistir";
type ErroresTransicion = Partial<Record<keyof FormTransicion, string>>;

const SIN_ERRORES_VENTA: ErroresVenta = {};
const SIN_ERRORES_PLAN: ErroresPlan = {};
const SIN_ERRORES_FILAS: ErroresFilas = {};
const SIN_ERRORES_CUOTA: ErroresCuota = {};
const SIN_ERRORES_COBRO: ErroresCobro = {};
const SIN_ERRORES_TRANSICION: ErroresTransicion = {};

/**
 * Lógica de la ficha de una venta (sin JSX): edición, plan de cuotas (vista previa del backend + filas editables),
 * cobros por cuota, transiciones de estado, devoluciones y adjuntos. Sólo el admin escribe; los cuatro roles leen.
 */
export function useVentaTab() {
  const { fid, membresia } = useContextoApp();
  const { id } = useParams();
  const navegar = useNavigate();
  const base = `/api/fideicomisos/${fid}/ventas`;
  const ruta = `${base}/${Number(id)}`;
  const esAdmin = membresia.rol === "admin";

  const ficha = useApiResource<VentaDetalle>(ruta);
  const v = ficha.data ?? null;
  const activa = v !== null && (v.estado === "reserva" || v.estado === "boleto");
  const conCobros = v !== null && v.cuotas.some((c) => c.cobros.length > 0);

  const [dialogo, setDialogo] = useState<Dialogo>(null);
  const editandoVenta = esAdmin && activa && dialogo?.tipo === "editar";
  const tcSugerido = useApiResource<TipoCambioSugerido>(esAdmin ? `/api/fideicomisos/${fid}/tipo-cambio-sugerido` : null);
  const cajas = useApiResource<Caja[]>(esAdmin ? `/api/fideicomisos/${fid}/cajas` : null);
  const compradores = useApiResource<CompradorListado[]>(editandoVenta ? `/api/fideicomisos/${fid}/compradores` : null);
  const unidades = useApiResource<Unidad[]>(editandoVenta ? `/api/fideicomisos/${fid}/unidades` : null);

  const [formV, setFormV] = useState<FormVenta>(() => formVenta());
  const [formP, setFormP] = useState<FormPlan>(() => formPlan());
  const [filas, setFilas] = useState<FilaCuota[]>([]);
  const [formQ, setFormQ] = useState<FormCuota>({ vencimiento: "" });
  const [formM, setFormM] = useState<FormCobro>(() => formCobro());
  const [formT, setFormT] = useState<FormTransicion>(() => formTransicion());
  const [mostrarErrores, setMostrarErrores] = useState(false);
  const [mostrarErroresPlan, setMostrarErroresPlan] = useState(false);
  const [cuotasAbiertas, setCuotasAbiertas] = useState<Set<number>>(() => new Set());

  // Los delete devuelven 204 (undefined): se mapean a `true` para distinguir éxito de fallo en `run`.
  const accionEditar = useAsyncAction((datos: object) => api.patch<VentaDetalle>(ruta, datos));
  const accionBorrar = useAsyncAction(() => api.delete<void>(ruta).then(() => true));
  const accionVistaPrevia = useAsyncAction((datos: { precio: string; modalidad: Modalidad; parametros: PayloadParametros }) => api.post<VistaPreviaPlan>(`${base}/plan/vista-previa`, datos));
  const accionPlan = useAsyncAction((cuotas: FilaCuota[]) => api.put<VentaDetalle>(`${ruta}/plan`, { cuotas }));
  const accionCuota = useAsyncAction((cuotaId: number, datos: FormCuota) => api.patch<VentaDetalle>(`${ruta}/cuotas/${cuotaId}`, datos));
  const accionMovimiento = useAsyncAction((destino: string, nuevo: boolean, datos: PayloadCobro) => (nuevo ? api.post<unknown>(destino, datos) : api.patch<unknown>(destino, datos)));
  const accionBorrarMovimiento = useAsyncAction((destino: string) => api.delete<void>(destino).then(() => true));
  const accionTransicion = useAsyncAction((camino: Transicion, datos: ReturnType<typeof aPayloadTransicion>) => api.post<VentaDetalle>(`${ruta}/${camino}`, datos));
  const accionSubir = useAsyncAction((destino: string, archivo: File) => {
    const form = new FormData();
    form.append("archivo", archivo);
    return api.upload<Adjunto>(destino, form);
  });
  const accionBorrarAdjunto = useAsyncAction((adjuntoId: number) => api.delete<void>(`/api/fideicomisos/${fid}/adjuntos/${adjuntoId}`).then(() => true));

  const monedaCuenta = v?.moneda_cuenta ?? "USD";
  const erroresV = validarVenta(formV);
  const erroresP = validarPlan(formP);
  const erroresF = validarFilas(filas);
  const erroresQ = validarCuota(formQ);
  const erroresM = validarCobro(formM, monedaCuenta);
  const erroresT = validarTransicion(formT, dialogo?.tipo === "desistir");

  const cerrar = () => setDialogo(null);
  const recargar = () => {
    cerrar();
    ficha.recargar();
  };
  const abrir = (d: NonNullable<Dialogo>) => {
    setMostrarErrores(false);
    setDialogo(d);
  };
  const hayErrores = (e: object) => Object.keys(e).length > 0;

  // ------------------------------------------------------------ venta

  const abrirEditar = () => {
    if (!v) return;
    setFormV(formVenta(hoyISO(), v));
    accionEditar.limpiarError();
    abrir({ tipo: "editar" });
  };

  /** En escriturada o desistida sólo se editan las notas (el backend rechaza el resto). */
  const guardarVenta = async (e: FormEvent) => {
    e.preventDefault();
    setMostrarErrores(true);
    if (activa && hayErrores(erroresV)) return;
    if (await accionEditar.run(activa ? aPayloadEdicionVenta(formV) : aPayloadNotasVenta(formV))) recargar();
  };

  const confirmarBorrar = async () => {
    if (await accionBorrar.run()) void navegar("/ventas");
  };

  // ------------------------------------------------------------ plan

  const abrirPlan = () => {
    if (!v) return;
    setFormP(formPlan());
    setFilas(filasDesdeVistaPrevia(v.cuotas));
    setMostrarErroresPlan(false);
    accionVistaPrevia.limpiarError();
    accionPlan.limpiarError();
    abrir({ tipo: "plan" });
  };

  const generar = async () => {
    setMostrarErroresPlan(true);
    if (!v || hayErrores(erroresP)) return;
    // El precio ya viene del backend como decimal normalizado ("100000.00"): se manda tal cual.
    const r = await accionVistaPrevia.run({ precio: v.precio, modalidad: v.modalidad, parametros: aParametrosPlan(formP) });
    if (r) setFilas(filasDesdeVistaPrevia(r.cuotas));
  };

  /** Siempre manda las filas: son lo que el admin vio (y pudo editar) antes de guardar. */
  const guardarPlan = async (e: FormEvent) => {
    e.preventDefault();
    setMostrarErrores(true);
    if (hayErrores(erroresF)) return;
    if (await accionPlan.run(aPayloadCuotas(filas))) recargar();
  };

  const abrirEditarCuota = (cuota: Cuota) => {
    setFormQ(formCuota(cuota));
    accionCuota.limpiarError();
    abrir({ tipo: "editar-cuota", cuota });
  };

  const guardarCuota = async (e: FormEvent) => {
    e.preventDefault();
    setMostrarErrores(true);
    if (dialogo?.tipo !== "editar-cuota" || hayErrores(erroresQ)) return;
    if (await accionCuota.run(dialogo.cuota.id, aPayloadCuota(formQ))) recargar();
  };

  // ------------------------------------------------------------ cobros y devoluciones (mismo formulario)

  const rutaCobros = (cuota: Cuota) => `${ruta}/cuotas/${cuota.id}/cobros`;

  const abrirCobro = (cuota: Cuota, cobro: Cobro | null = null) => {
    setFormM(formCobro(cobro ?? undefined, hoyISO(), tcSugerido.data?.tipo_cambio, monedaCuenta, cajaPorDefecto(cajas.data ?? [])));
    accionMovimiento.limpiarError();
    abrir({ tipo: "cobro", cuota, cobro });
  };

  const abrirDevolucion = (devolucion: Devolucion | null = null) => {
    setFormM(formCobro(devolucion ?? undefined, hoyISO(), tcSugerido.data?.tipo_cambio, monedaCuenta, cajaPorDefecto(cajas.data ?? [])));
    accionMovimiento.limpiarError();
    abrir({ tipo: "devolucion", devolucion });
  };

  const guardarMovimiento = async (e: FormEvent) => {
    e.preventDefault();
    setMostrarErrores(true);
    if (hayErrores(erroresM)) return;
    let destino: string;
    let nuevo: boolean;
    if (dialogo?.tipo === "cobro") {
      nuevo = dialogo.cobro === null;
      destino = nuevo ? rutaCobros(dialogo.cuota) : `${rutaCobros(dialogo.cuota)}/${dialogo.cobro?.id}`;
    } else if (dialogo?.tipo === "devolucion") {
      nuevo = dialogo.devolucion === null;
      destino = nuevo ? `${ruta}/devoluciones` : `${ruta}/devoluciones/${dialogo.devolucion?.id}`;
    } else return;
    if (await accionMovimiento.run(destino, nuevo, aPayloadCobro(formM, monedaCuenta))) recargar();
  };

  const confirmarBorrarMovimiento = async () => {
    let destino: string;
    if (dialogo?.tipo === "borrar-cobro") destino = `${rutaCobros(dialogo.cuota)}/${dialogo.cobro.id}`;
    else if (dialogo?.tipo === "borrar-devolucion") destino = `${ruta}/devoluciones/${dialogo.devolucion.id}`;
    else return;
    if (await accionBorrarMovimiento.run(destino)) recargar();
  };

  const abrirBorrarMovimiento = (d: Extract<Dialogo, { tipo: "borrar-cobro" | "borrar-devolucion" }>) => {
    accionBorrarMovimiento.limpiarError();
    abrir(d);
  };

  // ------------------------------------------------------------ transiciones

  const abrirTransicion = (tipo: Transicion) => {
    setFormT(formTransicion(hoyISO()));
    accionTransicion.limpiarError();
    abrir({ tipo });
  };

  const confirmarTransicion = async (e: FormEvent) => {
    e.preventDefault();
    setMostrarErrores(true);
    const tipo = dialogo?.tipo;
    if ((tipo !== "boleto" && tipo !== "escritura" && tipo !== "desistir") || hayErrores(erroresT)) return;
    if (await accionTransicion.run(tipo, aPayloadTransicion(formT, tipo === "desistir"))) recargar();
  };

  // ------------------------------------------------------------ adjuntos

  const subir = async (destino: string, archivo: File) => {
    accionBorrarAdjunto.limpiarError();
    if (await accionSubir.run(destino, archivo)) ficha.recargar();
  };

  const borrarAdjunto = async (a: Adjunto) => {
    accionSubir.limpiarError();
    if (await accionBorrarAdjunto.run(a.id)) ficha.recargar();
  };

  const alternarCuota = (cuotaId: number) =>
    setCuotasAbiertas((abiertas) => {
      const nuevas = new Set(abiertas);
      if (nuevas.has(cuotaId)) nuevas.delete(cuotaId);
      else nuevas.add(cuotaId);
      return nuevas;
    });

  const cambiador =
    <F>(set: (f: (prev: F) => F) => void) =>
    <K extends keyof F>(campo: K, valor: F[K]) =>
      set((f) => ({ ...f, [campo]: valor }));

  const cajaActual = dialogo?.tipo === "cobro" ? (dialogo.cobro?.caja_id ?? null) : dialogo?.tipo === "devolucion" ? (dialogo.devolucion?.caja_id ?? null) : null;

  return {
    esAdmin,
    venta: v,
    // Dónde entró (o de dónde salió) la plata (Mati, 2026-10-19); con una sola caja activa no se pregunta.
    opcionesCajaMovimiento: hayVariasCajas(cajas.data ?? []) ? opcionesCaja(cajas.data ?? [], cajaActual) : null,
    activa,
    cargando: ficha.loading && !ficha.data,
    errorCarga: ficha.data ? null : (ficha.error?.message ?? null),
    reintentar: ficha.recargar,
    dialogo,
    cerrar,
    // qué puede hacer el admin según el estado
    puede: {
      firmarBoleto: esAdmin && v?.estado === "reserva",
      escriturar: esAdmin && v?.estado === "boleto",
      desistir: esAdmin && activa,
      borrar: esAdmin && v?.estado === "reserva",
      armarPlan: esAdmin && activa && !conCobros,
      editarCobros: esAdmin && activa,
      devolver: esAdmin && v?.estado === "desistida",
      // Una cuota provisoria se cobra con el último índice cargado (queda congelado); sin ningún índice, no.
      cobrar: (c: Cuota) => esAdmin && activa && !c.sin_indice && montoPositivo(c.saldo),
      editarCuota: (c: Cuota) => esAdmin && activa && c.cobros.length === 0,
    },
    conCobros,
    // venta
    abrirEditar,
    formV,
    cambiarV: cambiador<FormVenta>(setFormV),
    erroresV: mostrarErrores && activa ? erroresV : SIN_ERRORES_VENTA,
    compradores: compradores.data ?? [],
    unidadesElegibles: v ? opcionesUnidadesDisponibles(unidades.data ?? [], v.unidades.map((u) => u.id)) : [],
    guardarVenta,
    guardandoVenta: accionEditar.loading,
    errorVenta: accionEditar.error,
    abrirBorrar: () => {
      accionBorrar.limpiarError();
      abrir({ tipo: "borrar" });
    },
    confirmarBorrar,
    borrando: accionBorrar.loading,
    errorBorrar: accionBorrar.error,
    // plan
    abrirPlan,
    formP,
    cambiarP: cambiador<FormPlan>(setFormP),
    erroresP: mostrarErroresPlan ? erroresP : SIN_ERRORES_PLAN,
    filas,
    setFilas,
    erroresF: mostrarErrores ? erroresF : SIN_ERRORES_FILAS,
    generar,
    generando: accionVistaPrevia.loading,
    errorVistaPrevia: accionVistaPrevia.error,
    guardarPlan,
    guardandoPlan: accionPlan.loading,
    errorPlan: accionPlan.error,
    // cuota
    abrirEditarCuota,
    formQ,
    cambiarQ: cambiador<FormCuota>(setFormQ),
    erroresQ: mostrarErrores ? erroresQ : SIN_ERRORES_CUOTA,
    guardarCuota,
    guardandoCuota: accionCuota.loading,
    errorCuota: accionCuota.error,
    cuotasAbiertas,
    alternarCuota,
    // cobros y devoluciones
    abrirCobro,
    abrirDevolucion,
    formM,
    cambiarM: cambiador<FormCobro>(setFormM),
    erroresM: mostrarErrores ? erroresM : SIN_ERRORES_COBRO,
    equivalente: equivalenteEnCuenta(formM.monto, formM.moneda, monedaCuenta, formM.tipo_cambio),
    guardarMovimiento,
    guardandoMovimiento: accionMovimiento.loading,
    errorMovimiento: accionMovimiento.error,
    abrirBorrarCobro: (cuota: Cuota, cobro: Cobro) => abrirBorrarMovimiento({ tipo: "borrar-cobro", cuota, cobro }),
    abrirBorrarDevolucion: (devolucion: Devolucion) => abrirBorrarMovimiento({ tipo: "borrar-devolucion", devolucion }),
    confirmarBorrarMovimiento,
    borrandoMovimiento: accionBorrarMovimiento.loading,
    errorBorrarMovimiento: accionBorrarMovimiento.error,
    // transiciones
    abrirTransicion,
    formT,
    cambiarT: cambiador<FormTransicion>(setFormT),
    erroresT: mostrarErrores ? erroresT : SIN_ERRORES_TRANSICION,
    confirmarTransicion,
    transicionando: accionTransicion.loading,
    errorTransicion: accionTransicion.error,
    // adjuntos
    rutaDescarga: (a: Adjunto) => `/api/fideicomisos/${fid}/adjuntos/${a.id}/descarga`,
    subirAVenta: (archivo: File) => subir(`${ruta}/adjuntos`, archivo),
    subirAlCobro: (cuota: Cuota, cobro: Cobro) => (archivo: File) => subir(`${rutaCobros(cuota)}/${cobro.id}/adjuntos`, archivo),
    borrarAdjunto,
    subiendo: accionSubir.loading,
    errorAdjuntos: accionSubir.error ?? accionBorrarAdjunto.error,
  };
}
